import {useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router-dom';

import {Box, Typography} from '@mui/material';

import {BasicTableCell, BasicTableRow} from '../../../../../components/BasicTable/BasicTableRow';
import {AppConfig} from '../../../../../lib/AppConfig';
import {getScoreLevelColors} from '../../../../../utils/getScoreLevelColors';
import type {WorkforceDashboardDepartmentHealth} from '../../types/workforceDashboardTypes';

export interface WorkforceDashboardDepartmentHealthTableRow extends WorkforceDashboardDepartmentHealth {
  id: string;
}

export type Props = WorkforceDashboardDepartmentHealthTableRow;

const DepartmentHealthBar = ({score}: {score: number | null}) => {
  const healthScore = score ?? 0;
  const healthColors = score !== null ? getScoreLevelColors(score) : null;

  return (
    <Box sx={{minWidth: 120, height: 6, borderRadius: '99px', bgcolor: 'action.hover', overflow: 'hidden'}}>
      <Box
        sx={{
          width: `${healthScore}%`,
          maxWidth: '100%',
          height: '100%',
          borderRadius: '99px',
          bgcolor: healthColors?.strokeColor ?? 'divider',
        }}
      />
    </Box>
  );
};

export const WorkforceDashboardDepartmentHealthRow = ({
  companyDepartmentId,
  companyDepartmentName,
  profileCount,
  gapCount,
  averageAlignmentScore,
}: Props) => {
  const navigate = useNavigate();
  const {t} = useTranslation('translation', {keyPrefix: 'workforceDashboardPage.sections.departmentHealth'});
  const healthColors = averageAlignmentScore !== null ? getScoreLevelColors(averageAlignmentScore) : null;

  const openDepartmentHealth = () => {
    const path = AppConfig.COMPANY_WORKFORCE_ANALYSIS_DEPARTMENT_HEALTH_PATH;
    navigate(`${path}?departmentId=${encodeURIComponent(companyDepartmentId)}`);
  };

  return (
    <BasicTableRow rowVariant='flat' hover onClick={openDepartmentHealth} sx={{cursor: 'pointer'}}>
      <BasicTableCell sx={{fontWeight: 500}}>{companyDepartmentName}</BasicTableCell>
      <BasicTableCell>
        <Typography variant='caption' sx={{color: 'text.secondary'}}>
          {t('summary', {profiles: profileCount, gaps: gapCount})}
        </Typography>
      </BasicTableCell>
      <BasicTableCell>
        <DepartmentHealthBar score={averageAlignmentScore} />
      </BasicTableCell>
      <BasicTableCell align='right'>
        <Typography
          variant='subtitle2'
          sx={{color: healthColors?.color ?? 'text.primary', whiteSpace: 'nowrap'}}
        >
          {averageAlignmentScore !== null ? `${averageAlignmentScore}%` : '-'}
        </Typography>
      </BasicTableCell>
    </BasicTableRow>
  );
};
